import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { ChartOptions } from 'chart.js';

interface TemperatureReading {
  timestamp: string;
  temperature: number;
}

interface BlockTempDrilldownProps {
  isOpen: boolean;
  onClose: () => void;
  plantId: string;
  blockId: string;
  date: string;
}

export const BlockTempDrilldown: React.FC<BlockTempDrilldownProps> = ({
  isOpen,
  onClose,
  plantId,
  blockId,
  date
}) => {
  const [data, setData] = useState<TemperatureReading[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState({
    min: 0,
    max: 0,
    avg: 0,
    current: 0
  });

  useEffect(() => {
    if (isOpen && blockId) {
      fetchBlockTemperatureHistory();
    }
  }, [isOpen, blockId, plantId, date]);

  const fetchBlockTemperatureHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch temperature data for specific block
      const response = await fetch(
        `http://localhost:8000/api/test/weather/?plant_id=${plantId}&date=${date}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch temperature data');
      }
      
      const result = await response.json();
      const blockData = result.blocks.find((b: any) => b.block_id === blockId);
      
      if (blockData) {
        const readings = blockData.data;
        setData(readings);
        
        // Calculate statistics
        const temperatures = readings.map((r: TemperatureReading) => r.temperature);
        const minTemp = Math.min(...temperatures);
        const maxTemp = Math.max(...temperatures);
        const avgTemp = temperatures.reduce((a, b) => a + b, 0) / temperatures.length;
        const currentTemp = temperatures[temperatures.length - 1] || 0;
        
        setStats({
          min: minTemp,
          max: maxTemp,
          avg: avgTemp,
          current: currentTemp
        });
      }
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const chartData = {
    labels: data.map(reading => 
      new Date(reading.timestamp).toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit' 
      })
    ),
    datasets: [
      {
        label: `${blockId} Temperature`,
        data: data.map(reading => reading.temperature),
        borderColor: '#3B82F6',
        backgroundColor: '#3B82F620',
        borderWidth: 2,
        pointRadius: 2,
        pointHoverRadius: 5,
        tension: 0.1,
        fill: true
      }
    ]
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: true,
        text: `${blockId} - Temperature History (${date})`
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            return `Temperature: ${context.parsed.y.toFixed(1)}°C`;
          }
        }
      }
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Time'
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Temperature (°C)'
        },
        min: Math.max(0, stats.min - 5),
        max: stats.max + 5
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {blockId} - Temperature Analysis
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600">Error: {error}</p>
            </div>
          ) : (
            <>
              {/* Statistics Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">{stats.current.toFixed(1)}°C</div>
                  <div className="text-sm text-gray-600">Current</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">{stats.avg.toFixed(1)}°C</div>
                  <div className="text-sm text-gray-600">Average</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-orange-600">{stats.max.toFixed(1)}°C</div>
                  <div className="text-sm text-gray-600">Maximum</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-purple-600">{stats.min.toFixed(1)}°C</div>
                  <div className="text-sm text-gray-600">Minimum</div>
                </div>
              </div>

              {/* Temperature Chart */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="h-80">
                  <Line data={chartData} options={options} />
                </div>
              </div>

              {/* Temperature Analysis */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white border rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Temperature Range Analysis</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Temperature Range:</span>
                      <span className="font-medium">{(stats.max - stats.min).toFixed(1)}°C</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Optimal Range (25-45°C):</span>
                      <span className={`font-medium ${
                        stats.avg >= 25 && stats.avg <= 45 ? 'text-green-600' : 'text-orange-600'
                      }`}>
                        {stats.avg >= 25 && stats.avg <= 45 ? 'Within Range' : 'Outside Range'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Peak Temperature:</span>
                      <span className={`font-medium ${
                        stats.max > 50 ? 'text-red-600' : stats.max > 45 ? 'text-orange-600' : 'text-green-600'
                      }`}>
                        {stats.max > 50 ? 'Critical' : stats.max > 45 ? 'High' : 'Normal'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-white border rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Performance Impact</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Efficiency Impact:</span>
                      <span className="font-medium">
                        {stats.avg > 45 ? '-2.5%' : stats.avg > 40 ? '-1.2%' : 'Minimal'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Thermal Stress:</span>
                      <span className={`font-medium ${
                        stats.max > 50 ? 'text-red-600' : stats.max > 45 ? 'text-orange-600' : 'text-green-600'
                      }`}>
                        {stats.max > 50 ? 'High' : stats.max > 45 ? 'Moderate' : 'Low'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Cooling Recommendation:</span>
                      <span className="font-medium">
                        {stats.max > 50 ? 'Required' : stats.max > 45 ? 'Advised' : 'Not Needed'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            Close
          </button>
          <button className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors">
            Export Data
          </button>
        </div>
      </div>
    </div>
  );
};